import { Alert, Empty, Modal, Skeleton, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useWABotSchedules } from "queries/botSchedule";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  botScheduleStatusColor,
  botScheduleStatusLabel,
} from "./botScheduleStatus";

const MeetingReminderListModal = ({ open, onClose, scheduleId, startTime }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";

  // Meeting sebelum jam 11 pagi → reminder pagi hari-H tidak dikirim.
  const isBeforeElevenAm = useMemo(() => {
    if (!startTime) return false;
    const time = dayjs(startTime, "HH:mm:ss");
    return time.isValid() && time.hour() < 11;
  }, [startTime]);

  const { data, isLoading, isError } = useWABotSchedules(
    {
      related_meeting_schedule_id: scheduleId,
      per_page: 100,
      sort_by: "send_time",
      order_by: "asc",
    },
    { enabled: open && Boolean(scheduleId) }
  );

  const list = data?.data ?? [];

  const columns = useMemo(
    () => [
      {
        title: t("meeting_management.reminder_message"),
        dataIndex: "message",
        key: "message",
        render: (message) =>
          message ? (
            <span className="whitespace-pre-line break-words text-sm leading-relaxed">
              {message}
            </span>
          ) : (
            <span className="text-neutral-300">—</span>
          ),
      },
      {
        title: t("meeting_management.reminder_send_time"),
        dataIndex: "send_time",
        key: "send_time",
        width: 150,
        render: (sendTime) =>
          sendTime ? (
            dayjs(sendTime).format("DD MMM YYYY HH:mm")
          ) : (
            <span className="text-neutral-300">—</span>
          ),
      },
      {
        title: t("meeting_management.reminder_status"),
        key: "status",
        width: 140,
        render: (_, record) => {
          const statusId = record.status?.id;
          const label =
            record.status?.name || botScheduleStatusLabel(statusId, locale);
          return (
            <Tag color={botScheduleStatusColor(statusId)} className="m-0">
              {label}
            </Tag>
          );
        },
      },
    ],
    [t, locale]
  );

  return (
    <Modal
      title={t("meeting_management.reminder_list_title")}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={640}
    >
      {isBeforeElevenAm && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("meeting_management.reminder_morning_skipped")}
        />
      )}
      <Skeleton loading={isLoading} active>
        {isError ? (
          <p className="my-6 text-center text-sm text-red-500">
            {t("meeting_management.reminder_load_failed")}
          </p>
        ) : (
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={list}
            pagination={false}
            scroll={{ y: "60vh" }}
            locale={{ emptyText: <Empty className="my-6" /> }}
          />
        )}
      </Skeleton>
    </Modal>
  );
};

export default MeetingReminderListModal;
